/**
 * Line icons, drawn rather than installed.
 *
 * An icon set would be a dependency, a bundle, and a visual language borrowed from someone
 * else's app. These are four shapes on the same 24-unit grid at the same stroke weight, so
 * they sit together — and the weight matches the tramline rules everything else is built
 * from (docs/design-direction.md §4).
 *
 * They take `currentColor`, so the nav's active and inactive states stay a matter of text
 * colour, and chrome stays uncoloured.
 */
interface IconProps {
  size?: number;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
});

/** Logging a match: a plus, because it is the one thing you come here to add. */
export function IconPlay({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 4v16M4 12h16" strokeWidth={2.4} strokeLinecap="square" />
    </svg>
  );
}

/** The board: ranked bars, tallest first, read as a standing rather than a chart. */
export function IconBoard({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 20h16" />
      <path d="M7 20V9M12 20V4M17 20v-7" />
    </svg>
  );
}

/** The confirmation queue: a tick, because the queue exists to be agreed with. */
export function IconMatches({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function IconMe({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

/** Events: a bracket — two seeds converging into one line, the shape of a knockout draw. */
export function IconEvents({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 6h4M4 18h4M8 6v5a3 3 0 0 0 3 3h1M8 18v-5a3 3 0 0 1 3-3h1" />
      <path d="M16 12h4" />
    </svg>
  );
}

/**
 * Sharing: something leaving the page, upward and out.
 *
 * The tray-with-an-arrow rather than the three-connected-dots. The dots are Android's glyph
 * and read as "social network" to everyone else; a thing going out of a box reads the same
 * on both, which matters for an app whose share button exists to reach WhatsApp.
 */
export function IconShare({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 4v11" />
      <path d="M8.5 7.5 12 4l3.5 3.5" />
      <path d="M6 12H5a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6a1 1 0 0 0-1-1h-1" />
    </svg>
  );
}

/** Confirmation, briefly, in place of the share glyph once a link is on the clipboard. */
export function IconTick({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * Running the event: sliders, not a cog.
 *
 * A cog says "settings", which these are not — they are the decisions an organiser makes
 * about one evening. Sliders also survive a 1.75 stroke at 20px, where a cog's teeth turn
 * into a smudge.
 */
export function IconManage({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 7h6M14 7h6M4 17h10M18 17h2" />
      <circle cx="12" cy="7" r="2" />
      <circle cx="16" cy="17" r="2" />
    </svg>
  );
}

/** Reordering a seed. A bare chevron: the arrow's tail would be a second idea. */
export function IconUp({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="m6 14 6-6 6 6" />
    </svg>
  );
}

export function IconDown({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="m6 10 6 6 6-6" />
    </svg>
  );
}

export function IconGoogle({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      />
    </svg>
  );
}
